import { useEffect, useRef } from 'react'
import { ChevronRight, X } from 'lucide-react'
import type { Actor, Incident } from '@/types/map'
import { dateUTC, incidentStatusVar, severityVar } from '../map-display-utils'
import { ProvenanceMiniLine, ReasonChips } from '../ProjectionProvenance'

export interface ActorDrawerProps {
  actor: Actor
  incidents: Incident[]
  onClose: () => void
  onOpenIncident: (incidentId: string) => void
}

export function ActorDrawer({ actor, incidents, onClose, onOpenIncident }: ActorDrawerProps) {
  const ref = useRef<HTMLElement>(null)

  useEffect(() => {
    ref.current?.focus()
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const distinctTargets = new Set(incidents.map((i) => i.targetId)).size
  // Render in the order supplied by the caller (MapNextPage already filters by
  // actor); the live feed owns chronological ordering.
  const recent = incidents

  return (
    <aside
      ref={ref}
      role="dialog"
      aria-label={`Actor ${actor.name}`}
      tabIndex={-1}
      className="fixed right-0 top-0 z-50 flex h-screen w-[480px] max-w-[92vw] flex-col border-l border-[color:var(--border-default)] bg-[color:var(--surface-elevated)] shadow-lg focus:outline-none"
      data-testid="actor-drawer"
    >
      {/* Header */}
      <header className="shrink-0 border-b border-[color:var(--border-subtle)] p-4">
        <div className="flex items-start justify-between">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-tertiary)]">
            Threat Actor Profile
          </span>
          <button
            onClick={onClose}
            aria-label="Close drawer"
            className="-mr-1 -mt-1 inline-flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--surface-tertiary)] hover:text-[color:var(--text-primary)]"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
        <h2 className="mt-2 font-heading text-xl leading-tight text-[color:var(--text-primary)]">
          {actor.name}
        </h2>
        {actor.aliases.length > 0 && (
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-tertiary)]">
            aka {actor.aliases.join(', ')}
          </p>
        )}
      </header>

      {/* Body */}
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 text-sm">
        <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--border-subtle)]">
          <Stat label="Observed" value={String(incidents.length)} />
          <Stat label="Targets" value={String(distinctTargets)} />
        </dl>

        <section>
          <h3 className="font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-tertiary)]">
            Recent activity
          </h3>
          {recent.length === 0 ? (
            <p className="mt-1.5 text-xs text-[color:var(--text-tertiary)]">
              No recent incidents linked to this actor.
            </p>
          ) : (
            <ul className="mt-1.5">
              {recent.map((inc) => (
                <li
                  key={inc.id}
                  data-testid="actor-incident-row"
                  onClick={() => onOpenIncident(inc.id)}
                  className="flex cursor-pointer flex-col gap-1 border-b border-[color:var(--border-subtle)] py-2 text-xs transition-colors last:border-0 hover:bg-[color:var(--surface-secondary)]"
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ background: severityVar(inc.severity) }}
                      aria-hidden
                    />
                    <span className="flex-1 truncate font-mono text-[color:var(--text-primary)]">
                      {inc.id}
                    </span>
                    <span
                      className="font-mono text-[9px] uppercase tracking-[0.12em]"
                      style={{ color: incidentStatusVar(inc.status) }}
                    >
                      {inc.status}
                    </span>
                    <span className="font-mono text-[color:var(--text-tertiary)]">
                      {dateUTC(inc.startedAt)}
                    </span>
                  </div>
                  <ReasonChips reasons={inc.unknownReasons} max={2} label={`Reasons for ${inc.id}`} />
                  {inc.provenance && inc.provenance.length > 0 && (
                    <span className="truncate font-mono text-[9px] uppercase tracking-[0.12em] text-[color:var(--text-tertiary)]">
                      <ProvenanceMiniLine provenance={inc.provenance} />
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Footer */}
      <footer className="shrink-0 border-t border-[color:var(--border-subtle)] p-4">
        <a
          href={`/threat-actors/${actor.id}`}
          className="inline-flex items-center gap-1 text-sm font-medium text-[color:var(--interactive-primary)] hover:underline"
        >
          View full profile
          <ChevronRight className="h-4 w-4" aria-hidden />
        </a>
      </footer>
    </aside>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-[color:var(--surface-secondary)] p-2.5">
      <dt className="font-mono text-[9px] uppercase tracking-[0.14em] text-[color:var(--text-tertiary)]">
        {label}
      </dt>
      <dd className="mt-0.5 font-mono text-base text-[color:var(--text-primary)]">{value}</dd>
    </div>
  )
}
